import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Link, useLocation } from 'react-router-dom';
import { toJS } from 'mobx';
import { useRootStore } from '@src/model';
import useGo from '@utils/navigate';
import throttle from 'lodash/throttle';
import { notification, Button } from '@ss/mtd-react';
import classnames from 'classnames';
import { logout } from '@service/login';
import { appendQuery2TargetUrlFromCurrentUrl } from '@utils/url';
import { scrollToTop } from '@utils/dom';
import { urlParse } from '@block/utils';
import { LxSiteClick } from '@utils/siteLx';
import SearchInput from '@elements/SearchInput';
import { isWeiXin } from '@utils/tools';
import { navList, dropNavList, pathInfo } from '../../Router/const';
import './index.scss';

export interface IProps {
  isStickyHeader?: boolean;
}

function Header(props: IProps) {
  // 登录相关
  const { loginStore } = useRootStore();
  const { go } = useGo();
  const location = useLocation();
  const { pathname } = location;
  const { highlightType } = urlParse(location.search);
  const { isStickyHeader = false } = props;
  const [isShowHeader, setIsShowHeader] = useState(true);
  const [isWhiteBg, setIsWhiteBg] = useState(false);
  // 退出按钮节流
  const isClickLogoutRef = useRef({ isClickLogout: false });

  // 用户信息
  const { isLogin, userInfo, resetLoginStatus } = loginStore || {};
  const { avatar } = toJS(userInfo) || {};

  // 判断是否为首页
  const isHomeOrBeiDouProgram = pathname === '/home' || pathname === pathInfo.beidouprogram;
  // 判断是否为ourTeam或职位详情页
  const isOurTeamPositon = pathname === '/about/ourTeam' || pathname === '/position';
  // 判断是否为社会招聘或校园招聘
  const isSocialCampus =
    pathname === '/social' || pathname === '/campus' || pathname === '/beidou' || pathname === '/qihang';
  // 搜索框是否展示
  const [isSearchVisble, setIsSearchVisble] = useState<Boolean>(false);

  // 社会招聘和校园招聘滚动显示搜索框
  const [isSearchFixed, setIsSearchFixed] = useState<Boolean>(false);

  // 监听滚动判断header应该隐藏还是显示
  useEffect(() => {
    if (isStickyHeader) {
      return undefined;
    }
    // 首页在此高度以上header透明
    const transparentHeight =
      Math.max(720, Math.min(document.documentElement.scrollWidth * 0.5625, document.documentElement.clientHeight)) *
        0.4 -
      196;
    // PC端使用document.documentElement.scrollTop 移动端使用 document.body.scrollTop；
    let beforeScrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    // 首页设置透明header
    if (isHomeOrBeiDouProgram && beforeScrollTop > transparentHeight) {
      setIsWhiteBg(true);
    } else {
      setIsWhiteBg(false);
    }

    const scrollFn = () => {
      const afterScrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      // beforeScrollTop safari浏览器有回弹效果 beforeScrollTop 可能为负值
      if (afterScrollTop - Math.abs(beforeScrollTop) > 0) {
        // 向上
        if (isShowHeader) {
          setIsShowHeader(false);
        }
      } else if (!isShowHeader) {
        // 向下
        setIsShowHeader(true);
      }
      // 根据scroll高度控制背景颜色（高度依据banner位高度）
      if (afterScrollTop > 462 || (isHomeOrBeiDouProgram && afterScrollTop > transparentHeight)) {
        setIsWhiteBg(true);
      } else {
        setIsWhiteBg(false);
      }
      beforeScrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    };
    const throttleScroll = throttle(scrollFn, 100);
    // 如果是关于美团 吸顶展示
    if (isOurTeamPositon) {
      setIsShowHeader(true);
    }
    document.addEventListener('scroll', throttleScroll);
    // 移除监听
    return function cleanup() {
      document.removeEventListener('scroll', throttleScroll);
    };
  }, [isShowHeader, isStickyHeader]);

  function goLogin() {
    go(`/login?redirectUrl=${encodeURIComponent(window.location.href)}`, {}, { isAppendQuery: false });
  }

  // 判断是否显示搜索框
  useEffect(() => {
    if (isOurTeamPositon || isSocialCampus) {
      setIsSearchVisble(true);
    } else {
      setIsSearchVisble(false);
    }
  }, [isOurTeamPositon, isSocialCampus]);

  // 校园招聘、社会招聘 根据scroll高度控制是否显示搜索框
  useEffect(() => {
    if (isOurTeamPositon) {
      setIsSearchFixed(true);
    } else {
      setIsSearchFixed(false);
    }
    const scrollFn = () => {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      if (isSocialCampus && scrollTop > 384) {
        setIsSearchFixed(true);
      } else if (isOurTeamPositon) {
        setIsSearchFixed(true);
      } else {
        setIsSearchFixed(false);
      }
    };
    const throttleScroll = throttle(scrollFn, 100);
    document.addEventListener('scroll', throttleScroll);
    // 移除监听
    return function cleanup() {
      document.removeEventListener('scroll', throttleScroll);
    };
  }, [pathname]);
  return (
    <div
      className={classnames('zp_header', {
        // eslint-disable-next-line camelcase
        zp_header_whiteBg: isWhiteBg || isStickyHeader,
        // eslint-disable-next-line camelcase
        zp_header_hide: !isShowHeader && !isStickyHeader,
        // eslint-disable-next-line camelcase
        zp_header_sticky: isStickyHeader,
        // eslint-disable-next-line camelcase
        zp_header_Home: !isWhiteBg && !isStickyHeader && isHomeOrBeiDouProgram,
      })}
    >
      <div className="zp_header_middle">
        <div className="logoWrap">
          <Link to={appendQuery2TargetUrlFromCurrentUrl('/home')}>
            <img
              className="logo"
              src={
                !isWhiteBg && !isStickyHeader && isHomeOrBeiDouProgram
                  ? require('@imgs/logo_white.svg')
                  : require('@imgs/logo.png')
              }
              alt="美团招聘"
            />
          </Link>
        </div>
        <div className="content">
          {Array.isArray(navList) &&
            navList.map((item) => (
              <div
                className={classnames('navWrap cursor_pointer', {
                  actived: (item.activeUrl || []).includes(window.location.pathname) || item.code === highlightType,
                })}
                key={item.name}
              >
                <Link
                  to={appendQuery2TargetUrlFromCurrentUrl(item.url)}
                  onClick={() => {
                    LxSiteClick({ bid: item.bid });
                    scrollToTop();
                  }}
                  target={item.target}
                >
                  <div className="navText">{item.name}</div>
                  <div className="line" />
                </Link>
              </div>
            ))}
        </div>
        <div className="personalCenter">
          {isLogin && (
            <div className="photoWrap">
              <img
                className="photo cursor_pointer"
                src={avatar || require('../../../imgs/avatar.png')}
                alt="头像"
                onClick={() => go('/personalCenter/resumeDetail')}
              />
              <div className="drop_wrapper">
                <ul className="drop_wrapper_content">
                  {Array.isArray(dropNavList) &&
                    dropNavList.map((item) => (
                      <li
                        className={classnames({
                          actived: item.code !== 'logout' && pathname === item.linkUrl,
                        })}
                        key={item.code}
                        onClick={async () => {
                          if (item.code === 'logout' && isClickLogoutRef.current.isClickLogout === false) {
                            LxSiteClick({ bid: 'b_ead_az52i2x6_mc' });
                            isClickLogoutRef.current.isClickLogout = true;
                            try {
                              await logout();
                            } catch (error) {
                              // 发生异常可以再次执行
                              isClickLogoutRef.current.isClickLogout = false;
                              // 发生异常终止后面流程
                              return;
                            }
                            notification.success({
                              title: '成功',
                              message: '账号退出成功',
                            });
                            resetLoginStatus();
                            go(item.linkUrl);
                            return;
                          }
                          go(item.linkUrl);
                        }}
                      >
                        {item.name}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          )}
          <Button
            className="langs-btn"
            onClick={() => {
              const { origin } = window.location;
              const isTest = origin.includes('it.test');
              const isWeixin = isWeiXin();
              const prodUrl = isWeixin ? 'https://careers.meituan.com/en' : 'https://careers.keeta-global.com';
              const url = !isTest ? prodUrl : `${origin}/en`;
              window.open(url, '_blank');
            }}
          >
            Global Careers
          </Button>
          {!isLogin && (
            <Button
              type="primary"
              className="loginBtn"
              onClick={() => {
                LxSiteClick({ bid: 'b_ead_wo5jjj2y_mc' });
                goLogin();
              }}
            >
              登录
            </Button>
          )}
        </div>
      </div>
      <div className={classnames(isSearchFixed ? '' : 'is_hidden')}>{isSearchVisble && <SearchInput />}</div>
    </div>
  );
}
export default observer(Header);
